import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Layout from "../../components/global/layout";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import swal from "sweetalert";
import { toast } from "react-toastify";
import {
  MODULE_CONFIG,
  MODULE_PERMISSIONS_SCHEMA,
  formatDate,
  formatTime,
  capitalize,
  countPermissions,
} from "./shared";
import { hasPermission } from "../../utils/permission";
import {
  FiArrowLeft,
  FiEdit3,
  FiTrash2,
  FiCheckCircle,
  FiXCircle,
  FiMail,
  FiShield,
  FiClock,
  FiUser,
  FiCheck,
  FiX,
} from "react-icons/fi";
import { HiOutlineUserGroup } from "react-icons/hi2";

export default function StaffDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);

  const canEdit = hasPermission(user, "staff", "edit");
  const canDelete = hasPermission(user, "staff", "delete");

  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch Staff Detail from API
  const fetchDetail = useCallback(async () => {
    loader(true);
    setLoading(true);

    try {
      const res = await ApiClient.get(`${MODULE_CONFIG.endpoints.detail}?id=${id}`);
      if (res?.success) {
        setStaff(res?.data || res?.staff || res);
      } else {
        toast.error("Failed to load staff details.");
        navigate(MODULE_CONFIG.routeBase);
      }
    } catch (err) {
      console.error("Error fetching staff dossier:", err);
      toast.error("Failed to load staff details.");
      navigate(MODULE_CONFIG.routeBase);
    } finally {
      setLoading(false);
      loader(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // Toggle Staff Status (active <-> deactive)
  const handleStatusChange = async (nextStatus) => {
    const staffName =
      `${staff?.firstName || ""} ${staff?.lastName || ""}`.trim() ||
      staff?.email ||
      "Staff Member";

    const isDeactivating = nextStatus === "deactive" || nextStatus === "inactive";
    const confirmed = await swal({
      title: `${isDeactivating ? "Deactivate" : "Activate"} Sub Administrator Account?`,
      text: `Are you sure you want to mark ${staffName} as ${nextStatus.toUpperCase()}?`,
      icon: isDeactivating ? "warning" : "info",
      buttons: ["Cancel", `Yes, ${isDeactivating ? "Deactivate" : "Activate"}`],
      dangerMode: isDeactivating,
    });

    if (!confirmed) return;

    loader(true);
    try {
      const res = await ApiClient.put(MODULE_CONFIG.endpoints.statusChange, {
        id,
        status: nextStatus,
      });

      if (res?.success) {
        toast.success(`Sub administrator account marked as ${nextStatus.toUpperCase()}.`);
        fetchDetail();
      } else {
        toast.error(res?.message || res?.error?.message || "Failed to update sub administrator status.");
      }
    } catch (err) {
      console.error("Status change error:", err);
      toast.error("Failed to update sub administrator status.");
    } finally {
      loader(false);
    }
  };

  // Delete Staff Member
  const handleDelete = async () => {
    const staffName =
      `${staff?.firstName || ""} ${staff?.lastName || ""}`.trim() ||
      staff?.email ||
      "Sub Administrator";

    const confirmed = await swal({
      title: "Delete Sub Administrator?",
      text: `Are you sure you want to permanently delete ${staffName}? All administrative privileges will be revoked immediately.`,
      icon: "warning",
      buttons: ["Cancel", "Yes, Delete Sub Administrator"],
      dangerMode: true,
    });

    if (!confirmed) return;

    loader(true);
    try {
      const res = await ApiClient.delete(`${MODULE_CONFIG.endpoints.delete}?id=${id}`);
      if (res?.success) {
        toast.success("Sub administrator deleted successfully.");
        navigate(MODULE_CONFIG.routeBase);
      } else {
        toast.error(res?.message || res?.error?.message || "Failed to delete sub administrator.");
      }
    } catch (err) {
      console.error("Delete staff error:", err);
      toast.error("Failed to delete sub administrator.");
    } finally {
      loader(false);
    }
  };

  if (loading || !staff) {
    return (
      <Layout>
        <div className="py-24 text-center space-y-3">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-slate-300 border-t-[#115B9D]" />
          <p className="text-xs font-bold text-slate-700">Loading sub administrator details...</p>
        </div>
      </Layout>
    );
  }

  const firstName = staff?.firstName || "";
  const lastName = staff?.lastName || "";
  const fullName = `${firstName} ${lastName}`.trim() || staff?.name || "Staff Member";
  const email = staff?.email || "";
  const status = (staff?.status || "active").toLowerCase();
  const createdAt = staff?.createdAt || staff?.created_at;
  const updatedAt = staff?.updatedAt || staff?.updated_at;

  const statusConfig = MODULE_CONFIG.statusMap[status] || {
    label: capitalize(status),
    bgClass: "bg-slate-100 text-slate-700 border-slate-300 font-bold",
    dotClass: "bg-slate-400",
  };

  const { granted, total } = countPermissions(staff?.permissions);
  const percentGranted = total > 0 ? Math.round((granted / total) * 100) : 0;

  return (
    <Layout>
      <div className="w-full max-w-full overflow-x-hidden space-y-5 sm:space-y-6 pb-16 antialiased">
        {/* ======================================================== */}
        {/* 1. TOP HEADER & BREADCRUMB ACTIONS                       */}
        {/* ======================================================== */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate(MODULE_CONFIG.routeBase)}
              className="p-2.5 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-900 border border-slate-300 transition-colors cursor-pointer shadow-xs shrink-0 flex items-center justify-center"
              title="Back to Sub Administrators List"
            >
              <FiArrowLeft className="w-5 h-5 text-slate-900" />
            </button>
            <div className="space-y-1 min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight truncate">
                  {fullName}
                </h1>
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-[11px] border whitespace-nowrap shadow-2xs ${statusConfig.bgClass}`}
                >
                  <span className={`w-2 h-2 rounded-full ${statusConfig.dotClass}`} />
                  {statusConfig.label}
                </span>
              </div>
              <p className="text-xs font-bold text-slate-700">
                Sub Administrator Dossier, Role Privileges & Assigned RBAC Matrix
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2.5 flex-wrap shrink-0">
            {canEdit && (
              <button
                type="button"
                onClick={() =>
                  handleStatusChange(status === "active" ? "deactive" : "active")
                }
                className={`inline-flex items-center gap-1.5 px-3.5 h-10 rounded-xl text-xs font-bold transition-all border cursor-pointer shadow-xs ${
                  status === "active"
                    ? "bg-rose-50 hover:bg-rose-100 text-rose-800 border-rose-300"
                    : "bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border-emerald-300"
                }`}
              >
                {status === "active" ? (
                  <>
                    <FiXCircle className="w-3.5 h-3.5" />
                    <span>Deactivate</span>
                  </>
                ) : (
                  <>
                    <FiCheckCircle className="w-3.5 h-3.5" />
                    <span>Activate</span>
                  </>
                )}
              </button>
            )}

            {canEdit && (
              <button
                type="button"
                onClick={() => navigate(`${MODULE_CONFIG.routeBase}/edit/${id}`)}
                className="inline-flex items-center gap-1.5 px-4 h-10 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer active:scale-[0.98]"
              >
                <FiEdit3 className="w-3.5 h-3.5" />
                <span>Edit Staff</span>
              </button>
            )}

            {canDelete && (
              <button
                type="button"
                onClick={handleDelete}
                className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-xl border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 text-xs font-bold transition-all cursor-pointer shadow-xs"
                title="Delete Staff"
              >
                <FiTrash2 className="w-3.5 h-3.5 text-rose-700" />
                <span>Delete</span>
              </button>
            )}
          </div>
        </div>

        {/* ======================================================== */}
        {/* 2. OVERVIEW ROW (PROFILE CARD + PERMISSION METER CARD)   */}
        {/* ======================================================== */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          {/* Card 1: Staff Profile (7 cols) */}
          <div className="lg:col-span-7 bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] flex flex-col justify-between space-y-4">
            <div className="space-y-4">
              <div className="flex items-center gap-2.5 border-b border-slate-200 pb-3">
                <div className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-800 border border-indigo-200 flex items-center justify-center font-bold">
                  <FiUser className="w-4 h-4" />
                </div>
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                  Staff Account Profile
                </h3>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
                <div className="w-14 h-14 rounded-2xl bg-[#115B9D] text-white flex items-center justify-center font-black text-lg shrink-0 shadow-md">
                  {fullName.slice(0, 2).toUpperCase()}
                </div>
                <div className="space-y-1 min-w-0">
                  <h4 className="text-sm font-black text-slate-900 truncate">{fullName}</h4>
                  <a
                    href={`mailto:${email}`}
                    className="text-xs font-bold text-[#115B9D] hover:underline flex items-center gap-1.5 truncate"
                  >
                    <FiMail className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{email}</span>
                  </a>
                  <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-indigo-50 text-indigo-900 border border-indigo-200">
                    <FiShield className="w-3 h-3 text-indigo-700" />
                    <span>Sub-Admin Staff Account</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1 text-xs">
                <div className="p-3 rounded-xl bg-white border border-slate-200 space-y-1">
                  <span className="text-[10px] font-bold text-slate-500 uppercase block">
                    Account Created
                  </span>
                  <div className="font-bold text-slate-900">
                    {createdAt ? formatDate(createdAt) : "—"}
                  </div>
                  {createdAt && (
                    <span className="text-[11px] font-medium text-slate-600 block">
                      {formatTime(createdAt)}
                    </span>
                  )}
                </div>

                <div className="p-3 rounded-xl bg-white border border-slate-200 space-y-1">
                  <span className="text-[10px] font-bold text-slate-500 uppercase block">
                    Last Profile Update
                  </span>
                  <div className="font-bold text-slate-900">
                    {updatedAt ? formatDate(updatedAt) : "—"}
                  </div>
                  {updatedAt && (
                    <span className="text-[11px] font-medium text-slate-600 block">
                      {formatTime(updatedAt)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: Permissions Progress Meter (5 cols) */}
          <div className="lg:col-span-5 bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] flex flex-col justify-between space-y-4">
            <div className="space-y-4">
              <div className="flex items-center gap-2.5 border-b border-slate-200 pb-3">
                <div className="w-7 h-7 rounded-lg bg-purple-50 text-purple-800 border border-purple-200 flex items-center justify-center font-bold">
                  <FiShield className="w-4 h-4" />
                </div>
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                  Privileges & Access Level
                </h3>
              </div>

              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-700">Platform Coverage</span>
                  <span className="text-sm font-black text-[#115B9D]">
                    {percentGranted}% Granted
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                  <div
                    className="bg-[#115B9D] h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${percentGranted}%` }}
                  />
                </div>

                <div className="flex items-center justify-between text-xs pt-1">
                  <span className="font-medium text-slate-600">Active Actions:</span>
                  <span className="font-black text-slate-900">
                    {granted} of {total} Actions
                  </span>
                </div>
              </div>

              <div className="p-3 rounded-xl bg-white border border-slate-200 text-xs space-y-1">
                <span className="text-[10px] font-bold text-slate-500 uppercase block">
                  Security Classification
                </span>
                <p className="font-bold text-slate-800 text-xs">
                  {percentGranted === 100
                    ? "Full Access (All administration modules unrestricted)"
                    : percentGranted >= 50
                    ? "Standard Operator (Elevated multi-module operational privileges)"
                    : "Restricted Access (Limited module privileges)"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ======================================================== */}
        {/* 3. GRANULAR RBAC PERMISSIONS AUDIT MATRIX               */}
        {/* ======================================================== */}
        <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-5">
          <div className="flex items-center justify-between gap-3 border-b border-slate-200 pb-3 flex-wrap">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-200 flex items-center justify-center font-bold">
                <FiCheckCircle className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                  Granular Module Permissions Breakdown
                </h3>
                <p className="text-xs font-medium text-slate-600">
                  Audit report of active and restricted capabilities for this staff user.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {MODULE_PERMISSIONS_SCHEMA.map((mod) => {
              const currentModule = staff?.permissions?.[mod.key] || {};
              const hasAnyGranted = mod.actions.some((act) => Boolean(currentModule[act.key]));

              return (
                <div
                  key={mod.key}
                  className={`p-4 rounded-xl border space-y-3 ${
                    hasAnyGranted
                      ? "bg-slate-50/70 border-slate-300"
                      : "bg-white border-slate-200 opacity-75"
                  }`}
                >
                  <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                    <h4 className="text-xs font-black text-slate-900 uppercase tracking-wide">
                      {mod.label}
                    </h4>
                    <span
                      className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-md border ${
                        hasAnyGranted
                          ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                          : "bg-slate-100 text-slate-600 border-slate-300"
                      }`}
                    >
                      {hasAnyGranted ? "Access Enabled" : "No Access"}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                    {mod.actions.map((act) => {
                      const isGranted = Boolean(currentModule[act.key]);
                      return (
                        <div
                          key={act.key}
                          className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs font-bold ${
                            isGranted
                              ? "bg-emerald-50 text-emerald-950 border-emerald-300"
                              : "bg-slate-100/70 text-slate-500 border-slate-200 line-through"
                          }`}
                        >
                          {isGranted ? (
                            <FiCheck className="w-3.5 h-3.5 text-emerald-700 shrink-0 stroke-[3]" />
                          ) : (
                            <FiX className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          )}
                          <span className="truncate">{act.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
}
